import React, { useEffect, useState } from 'react';
import { ConfigProvider, Modal, theme as antdTheme } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectCurrentSpace, selectPersonalUserId } from '@/store/selectors/spaceSelectors';
import { resolveBrand } from '@/config/brand';
import { subscribeSpaceSwitch } from '@/utils/spaceSyncChannel';

/**
 * 空间切换跨标签提示守卫（SharedWorker 中转，挂于 fullScreenRoutes 的 canvas/director 页）
 *
 * 场景：home 标签切换账户/空间后，本标签（canvas 等）仍持旧空间内存态。收到他标签
 * 广播的新身份 id（团队=teamId，个人=personalUserId），与本标签 redux 身份比对：
 * 不一致 → 弹「当前会话已过期，身份已切换」，点「返回首页」整页跳转重建状态。
 * 弹窗必须 JSX 渲染（React 19 下 antd 静态 Modal.warning 依赖 ReactDOM.render 已失效）。
 */
interface SpaceSyncGuardProps {
  children?: React.ReactNode;
}

export const SpaceSyncGuard: React.FC<SpaceSyncGuardProps> = ({ children }) => {
  const { t } = useTranslation();
  const [expired, setExpired] = useState(false);
  const currentSpace = useSelector(selectCurrentSpace);
  const personalUserId = useSelector(selectPersonalUserId);

  useEffect(() => {
    // 本标签当前身份：团队取 teamId，个人取 personalUserId
    const ownIdentityId = currentSpace ? currentSpace.teamId : personalUserId || null;
    // debug（20261030 排查「历史页偶发收不到过期弹窗」）：订阅/比对全过程落日志
    console.log('[空间同步守卫] 订阅, 本标签身份:', ownIdentityId, {
      在团队空间: !!currentSpace,
      teamId: currentSpace?.teamId ?? null,
      personalUserId,
    });
    return subscribeSpaceSwitch(msg => {
      const differ = (msg.identityId ?? null) !== ownIdentityId;
      console.log('[空间同步守卫] 身份比对:', {
        收到新身份: msg.identityId ?? null,
        本标签身份: ownIdentityId,
        不一致: differ,
      });
      if (differ) {
        console.warn('[空间同步守卫] 身份不一致 → 弹「会话已过期」弹窗');
        setExpired(true);
      }
    });
  }, [currentSpace, personalUserId]);

  return (
    <>
      {children}
      {/* 守卫可能挂在无暗色 ConfigProvider 的路由（如 canvas 全屏页）外层，
          自带暗色主题保证各页弹窗风格统一：深灰浮层 + 品牌橙主按钮 */}
      <ConfigProvider
        theme={{
          algorithm: antdTheme.darkAlgorithm,
          token: {
            colorBgElevated: '#2B2B2B',
            borderRadius: 12,
            colorPrimary: '#FF6A39',
          },
        }}
      >
        <Modal
          open={expired}
          title={t('space.sessionExpiredTitle')}
          okText={t('space.backHome')}
          cancelButtonProps={{ style: { display: 'none' } }}
          closable={false}
          maskClosable={false}
          zIndex={3000}
          onOk={() => {
            window.location.href = resolveBrand().defaultRoute || '/home';
          }}
        >
          {t('space.identitySwitchedContent')}
        </Modal>
      </ConfigProvider>
    </>
  );
};

export default SpaceSyncGuard;
